import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LeadIntegrationConnection {
  id: string;
  provider: string;
  firm_id: string;
  external_account_id: string;
  webhook_subscription_id: string | null;
  default_mapping_id: string | null;
  active: boolean;
  created_at: string;
  modified_at: string;
}

export interface CreateLeadIntegrationConnectionPayload {
  provider: string;
  firm_id: string;
  external_account_id: string;
  auth_config_ref: string;
  webhook_secret_ref: string;
  private_key_ref?: string | null;
  webhook_subscription_id?: string | null;
  default_mapping_id?: string | null;
  active?: boolean;
}

export interface Lead {
  id: string;
  provider: string;
  firm_id: string;
  connection_id: string;
  external_lead_id: string;
  status: LeadStatus;
  source_tag: string;
  raw_payload: Record<string, unknown>;
  normalized_payload: NormalizedLeadPayload;
  proposed_intake_payload: Record<string, unknown>;
  intake_id: string | null;
  intake_status: string | null;
  referring_firm_id: string | null;
  referring_firm_name: string | null;
  matter_id: string | null;
  live_transfer_requested: boolean;
  created_at: string;
  modified_at: string;
}

export type LeadStatus =
  | 'new'
  | 'reviewing'
  | 'intake_created'
  | 'converted'
  | 'rejected'
  | 'closed'
  | 'error';

export interface NormalizedLeadPayload {
  client_full_name: string;
  client_phone: string;
  client_email: string;
  client_address: string;
  client_dob: string;
  incident_date: string;
  incident_state: string;
  incident_location: string;
  incident_type: string;
  incident_description: string;
  case_type: string;
  notes: string;
  custom_fields: Record<string, unknown>;
}

export interface LeadLexamicaMetadata {
  id: string;
  lead_id: string;
  org_id: string;
  mapping_id: string | null;
  subscription_id: string | null;
  invitation_id: string | null;
  case_id: string;
  case_type: string | null;
  incident_date: string | null;
  incident_date_raw: string | null;
  incident_state: string | null;
  custom_fields: Record<string, unknown>;
  created_at: string;
  modified_at: string;
}

export interface LeadDetail {
  lead: Lead;
  lexamica_metadata: LeadLexamicaMetadata | null;
}

export interface LeadListParams {
  firm_id?: string;
  status?: LeadStatus;
  provider?: string;
}

export interface CreateSubscriptionPayload {
  id: string;
  webhook_url: string;
  event?: string;
}

// ---------------------------------------------------------------------------
// API Client
// ---------------------------------------------------------------------------

export const leadIntegrationApiClient = createApi({
  reducerPath: 'leadIntegrationApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Lead', 'Leads', 'LeadConnection', 'LeadConnections'] as const,
  endpoints: (builder) => ({
    // Connections
    getConnections: builder.query<LeadIntegrationConnection[], string>({
      query: (firmId) => ({
        url: 'api/integrations/leads/connections/',
        params: { firm_id: firmId },
      }),
      providesTags: ['LeadConnections'],
    }),

    getConnection: builder.query<LeadIntegrationConnection, string>({
      query: (id) => `api/integrations/leads/connections/${id}/`,
      providesTags: (_result, _error, id) => [
        { type: 'LeadConnection', id },
      ],
    }),

    createConnection: builder.mutation<
      LeadIntegrationConnection,
      CreateLeadIntegrationConnectionPayload
    >({
      query: (body) => ({
        url: 'api/integrations/leads/connections/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LeadConnections'],
    }),

    deactivateConnection: builder.mutation<LeadIntegrationConnection, string>({
      query: (id) => ({
        url: `api/integrations/leads/connections/${id}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'LeadConnection', id },
        'LeadConnections',
      ],
    }),

    createSubscription: builder.mutation<
      { subscription_id: string },
      CreateSubscriptionPayload
    >({
      query: ({ id, ...body }) => ({
        url: `api/integrations/leads/connections/${id}/create-subscription/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'LeadConnection', id },
        'LeadConnections',
      ],
    }),

    // Leads
    getLeads: builder.query<Lead[], LeadListParams>({
      query: ({ firm_id, status, provider }) => ({
        url: 'api/integrations/leads/',
        params: {
          ...(firm_id ? { firm_id } : {}),
          ...(status ? { status } : {}),
          ...(provider ? { provider } : {}),
        },
      }),
      providesTags: ['Leads'],
    }),

    getLead: builder.query<LeadDetail, string>({
      query: (id) => `api/integrations/leads/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Lead', id }],
    }),

    updateLeadStatus: builder.mutation<
      Lead,
      { id: string; status: LeadStatus; reason?: string }
    >({
      query: ({ id, status, reason }) => ({
        url: `api/integrations/leads/${id}/status/`,
        method: 'POST',
        body: { status, ...(reason ? { reason } : {}) },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Lead', id },
        'Leads',
      ],
    }),

    createIntakeDraft: builder.mutation<LeadDetail, string>({
      query: (leadId) => ({
        url: `api/integrations/leads/${leadId}/create-intake-draft/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, leadId) => [
        { type: 'Lead', id: leadId },
        'Leads',
      ],
    }),

    linkIntake: builder.mutation<
      LeadDetail,
      { leadId: string; intakeId: string }
    >({
      query: ({ leadId, intakeId }) => ({
        url: `api/integrations/leads/${leadId}/link-intake/`,
        method: 'POST',
        body: { intake_id: intakeId },
      }),
      invalidatesTags: (_result, _error, { leadId }) => [
        { type: 'Lead', id: leadId },
        'Leads',
      ],
    }),
  }),
});

export const {
  useGetConnectionsQuery,
  useGetConnectionQuery,
  useCreateConnectionMutation,
  useDeactivateConnectionMutation,
  useCreateSubscriptionMutation,
  useGetLeadsQuery,
  useGetLeadQuery,
  useUpdateLeadStatusMutation,
  useCreateIntakeDraftMutation,
  useLinkIntakeMutation,
} = leadIntegrationApiClient;
